import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import StudentLayout from '../../layouts/StudentLayout';
import { dataService } from '../../../services/dataService';
import { LearningPath, Standard, LearningPathStandard, Student, StandardProgress } from '../../../data/types';

// Extended interfaces for UI display purposes
interface ExtendedLearningPath extends LearningPath {
  progress?: number;
}

interface ExtendedLearningPathStandard extends LearningPathStandard {
  completed?: boolean;
}

const StudentLearningPaths: React.FC = () => {
  const [learningPaths, setLearningPaths] = useState<ExtendedLearningPath[]>([]);
  const [loading, setLoading] = useState(true);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [currentStudent, setCurrentStudent] = useState<Student | null>(null);

  useEffect(() => {
    // Set student session flag when component mounts
    localStorage.setItem('isStudentSession', 'true');
    
    const fetchData = async () => {
      try {
        // Get the current student (using the first student for demo)
        const students = await dataService.getStudents();
        const student = students[0]; // Get the first student with assigned learning paths
        setCurrentStudent(student);
        
        // Fetch learning paths
        let paths = await dataService.getLearningPaths();
        
        // Filter paths to only include those assigned to the student
        if (student?.assignedLearningPaths) {
          paths = paths.filter(path => student.assignedLearningPaths?.includes(path.id));
          
          // Calculate progress for each path based on student's progress
          paths = paths.map(path => {
            const pathStandards = path.standards;
            let completedCount = 0;
            
            // Enhanced path with progress information and completed standards
            const enhancedPath: ExtendedLearningPath = {
              ...path,
              standards: pathStandards.map(standard => {
                const standardProgress = student.progress[standard.standardId];
                const isCompleted = standardProgress?.learningPhaseStatus === 'Completed';
                
                if (isCompleted) {
                  completedCount++;
                }
                
                return {
                  ...standard,
                  completed: isCompleted
                };
              }),
              progress: pathStandards.length > 0 ? Math.round((completedCount / pathStandards.length) * 100) : 0
            };
            
            return enhancedPath;
          });
        }
        
        setLearningPaths(paths);
        
        // Fetch standards for details
        const allStandards = await dataService.getStandards();
        setStandards(allStandards);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching learning paths:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Group paths by grade level
  const pathsByGrade = learningPaths.reduce((acc, path) => {
    const grade = path.grade || 'Unspecified';
    if (!acc[grade]) {
      acc[grade] = [];
    }
    acc[grade].push(path);
    return acc;
  }, {} as Record<string, ExtendedLearningPath[]>);
  
  // Sort grade levels
  const sortedGrades = Object.keys(pathsByGrade).sort((a, b) => {
    // Extract numeric part if it exists
    const numA = parseInt(a.match(/\d+/)?.[0] || '0');
    const numB = parseInt(b.match(/\d+/)?.[0] || '0');
    return numA - numB;
  });

  return (
    <StudentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Learning Paths</h1>
          <p className="mt-2 text-lg text-gray-600">
            Explore your learning paths and continue your journey
          </p>
          {currentStudent && (
            <div className="mt-4 p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-800">
                <span className="font-medium">Current student:</span> {currentStudent.name} | 
                <span className="font-medium">Grade:</span> {currentStudent.grade} | 
                <span className="font-medium">Learning paths:</span> {currentStudent.assignedLearningPaths?.length || 0}
              </p>
            </div>
          )}
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <>
            {sortedGrades.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="text-xl font-medium text-gray-900 mb-2">No Learning Paths Available</h3>
                <p className="text-gray-600">
                  You don't have any learning paths assigned yet. Please check back later.
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {sortedGrades.map((grade) => (
                  <div key={grade} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="bg-purple-600 px-6 py-4">
                      <h2 className="text-xl font-semibold text-white">Grade {grade}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                      {pathsByGrade[grade].map((pathItem) => {
                        const path = pathItem as ExtendedLearningPath;
                        return (
                          <div key={path.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
                            <div className="p-5">
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">{path.name}</h3>
                              <p className="text-gray-600 text-sm mb-4">{path.description || 'Explore this learning path to improve your math skills.'}</p>
                              <div className="mb-4">
                                <div className="flex items-center">
                                  <div className="text-xs font-medium text-gray-500 mr-2">Progress:</div>
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-green-500 h-2 rounded-full" 
                                      style={{ width: `${path.progress || 0}%` }}
                                    ></div>
                                  </div>
                                  <div className="text-xs font-medium text-gray-500 ml-2">{path.progress || 0}%</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              {path.standards.slice(0, 3).map((standardItem) => {
                                const standard = standardItem as ExtendedLearningPathStandard;
                                const standardDetails = standards.find(s => s.id === standard.standardId);
                                return (
                                  <Link 
                                    key={standard.standardId} 
                                    to={`/student/learning/${path.id}/${standard.standardId}`}
                                    className="block p-3 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors duration-200"
                                  >
                                    <div className="flex items-center">
                                      <div className="mr-3">
                                        {standard.completed ? (
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                          </svg>
                                        ) : (
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">{standardDetails?.code || standard.standardId}</div>
                                        <div className="text-xs text-gray-600 truncate">{standardDetails?.description || 'Standard description'}</div>
                                      </div>
                                    </div>
                                  </Link>
                                );
                              })}
                              
                              {path.standards.length > 3 && (
                                <div className="text-center pt-2">
                                  <span className="text-sm text-purple-600 font-medium">
                                    +{path.standards.length - 3} more standards
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </StudentLayout>
  );
};

export default StudentLearningPaths;
